import Joi from 'joi';

import _ from 'underscore';
import getConfig from 'next/config';

import { api as apiRequest } from '@culture/common-helpers/request';
import { addCleanCache } from '@culture/common-helpers/params';
import { prepareBlocks } from '@culture/common-helpers/page';
import { getAdditionalBlocks } from '@culture/common-helpers/logic/blocks';
import { fieldsHash } from 'utils/helpers/catalogs';

import CommonConcertsTemplate from 'pageTemplates/music/concerts/common';

const { serverRuntimeConfig } = getConfig();
const { concertsThemesRubric } = serverRuntimeConfig.entities;

const CommonConcertsPage = (props) => <CommonConcertsTemplate {...props} />;

const getBlocks = (cleanCache, rubrics, persons) => {
	const blocks = {
		concertsOnline: {
			resource: 'movies',
			limit: 8,
			params: addCleanCache(
				{
					statuses: ['published'],
					types: ['concert'],
					fields: fieldsHash.concerts.join(),
					cached: true
				},
				cleanCache
			),
			sort: '-publishDate'
		},
		themes: {
			resource: 'publications',
			limit: 6,
			params: addCleanCache(
				{
					statuses: ['published'],
					fields: [
						'title',
						'shortText',
						'thumbnailFile',
						'type',
						'name',
						'type'
					].join(),
					mainRubric: concertsThemesRubric,
					cached: true
				},
				cleanCache
			),
			sort: '-publishDate'
		}
	};

	rubrics.forEach((rubric) => {
		blocks[`rubric_${rubric.name}`] = {
			resource: 'movies',
			limit: 10,
			params: addCleanCache(
				{
					statuses: ['published'],
					types: ['concert'],
					rubricPath: rubric.path,
					fields: fieldsHash.concerts.join(),
					cached: true
				},
				cleanCache
			),
			sort: '-publishDate'
		};
	});

	if (persons.length) {
		const person = persons[0];

		blocks[`person_${person.name}`] = {
			resource: 'movies',
			limit: 10,
			params: addCleanCache(
				{
					statuses: ['published'],
					types: ['concert'],
					composers: [person.name],
					fields: fieldsHash.concerts.join(),
					expand: ['participants.type'],
					cached: true
				},
				cleanCache
			),
			sort: '-publishDate'
		};
	}

	return blocks;
};

export async function getServerSideProps({ query }) {
	const validationSchema = Joi.object().keys({
		cleanCache: Joi.boolean()
	});

	const params = Joi.attempt(query, validationSchema, {
		stripUnknown: true
	});

	const commonConcertsRubricPage = await apiRequest(
		'rubricPages/page',
		addCleanCache(
			{
				rubricPath: 'music/concerts/common',
				blockFields: {
					editorsChoice: [
						'title',
						'name',
						'thumbnailFile',
						'shortText',
						'institute',
						'duration',
						'year',
						'type',
						'materials'
					].join(),
					concertsRubrics: fieldsHash.rubrics.join(),
					concertsPersons: ['title', 'name'].join()
				}
			},
			params.cleanCache
		)
	);

	const blockData = getBlocks(
		params.cleanCache,
		commonConcertsRubricPage.blocks.concertsRubrics,
		commonConcertsRubricPage.blocks.concertsPersons
	);

	const [additionalBlocks, filters] = await Promise.all([
		getAdditionalBlocks(blockData),
		apiRequest(
			'catalogFilters/music/concerts',
			addCleanCache(
				{
					cached: true
				},
				params.cleanCache
			)
		)
	]);

	const blocks = {
		...additionalBlocks,
		editorsChoice: commonConcertsRubricPage.blocks.editorsChoice
	};

	const preparedBlocks = prepareBlocks(
		_(blocks).omit('concertsRubrics', 'concertsPersons'),
		commonConcertsRubricPage.blocks.concertsRubrics,
		commonConcertsRubricPage.blocks.concertsPersons
	);

	return {
		props: {
			blocks: preparedBlocks || {},
			rubrics: filters.rubrics
		}
	};
}

export default CommonConcertsPage;
